import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  FileText, Receipt, ArrowRight, Loader2, AlertTriangle, Plus, Trash2,
  CheckCircle2, Hash,
} from 'lucide-react';
import { parseFR } from '@/lib/utils';
import NumInputFR from '@/components/NumInputFR';

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0).toFixed(2);

/**
 * Modal: Convert a signed Devis into a Facture.
 *  - Requires a bon-de-commande (BC) number (mandatory for Chorus Pro)
 *  - Asks if the facture is identical to the devis
 *  - If not, shows an editable line table (qty / PU / TVA / remise)
 *  - On submit, calls /api/devis/{id}/convert-to-facture and navigates to the facture
 */
export default function ConvertDevisToFactureDialog({ open, onOpenChange, devis }) {
  const navigate = useNavigate();
  const [bc, setBc] = useState('');
  const [engagement, setEngagement] = useState('');
  const [identical, setIdentical] = useState('yes'); // 'yes' | 'no'
  const [notes, setNotes] = useState('');
  const [delaiPaiement, setDelaiPaiement] = useState(30);
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && devis) {
      setBc('');
      setEngagement('');
      setIdentical('yes');
      setNotes('');
      setDelaiPaiement(30);
      setLines((devis.lignes || []).map((l) => ({
        designation: l.designation || '',
        description: l.description || '',
        quantite: parseFloat(l.quantite) || 0,
        unite: l.unite || 'U',
        prix_unitaire_ht: parseFloat(l.prix_unitaire_ht ?? l.prix_unitaire) || 0,
        tva_pct: parseFloat(l.tva_pct ?? l.tva_rate) || 20,
        remise_pct: parseFloat(l.remise_pct ?? l.remise) || 0,
      })));
    }
  }, [open, devis]);

  const totals = useMemo(() => {
    if (identical === 'yes') return devis?.totaux || {};
    let ht = 0, tva = 0;
    lines.forEach((l) => {
      const lineHT = (l.quantite || 0) * (l.prix_unitaire_ht || 0) * (1 - (l.remise_pct || 0) / 100);
      ht += lineHT;
      tva += lineHT * ((l.tva_pct || 0) / 100);
    });
    return { total_ht: ht, total_tva: tva, total_ttc: ht + tva };
  }, [identical, lines, devis]);

  const updateLine = (idx, key, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
  };

  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const addLine = () => setLines((prev) => [...prev, {
    designation: '', description: '', quantite: 1, unite: 'U',
    prix_unitaire_ht: 0, tva_pct: 20, remise_pct: 0,
  }]);

  const submit = async () => {
    if (!bc.trim()) {
      toast.error("Le numéro du bon de commande est obligatoire");
      return;
    }
    if (identical === 'no' && lines.length === 0) {
      toast.error("Ajoutez au moins une ligne à la facture");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        numero_bon_commande: bc.trim(),
        identique_au_devis: identical === 'yes',
        delai_paiement_jours: Number(delaiPaiement) || 30,
        notes_changements: notes,
      };
      if (identical === 'no') {
        payload.lignes = lines;
      }
      const r = await api.post(`/devis/${devis.devis_id}/convert-to-facture`, payload);
      toast.success(`Facture ${r.data.facture.numero} créée`);
      onOpenChange(false);
      navigate(`/factures/${r.data.facture.facture_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la conversion");
    }
    setSubmitting(false);
  };

  if (!devis) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="convert-facture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-600" />
            Convertir le devis en facture
          </DialogTitle>
          <DialogDescription>
            Devis <b>{devis.devis_id}</b> · {devis.client_info?.nom || '—'} · {fmt(devis.totaux?.total_ttc || devis.montant_total || 0)} € TTC
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* BC + délai paiement + engagement */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" />
                N° du bon de commande <span className="text-red-500">*</span>
              </Label>
              <Input
                value={bc}
                onChange={(e) => setBc(e.target.value)}
                placeholder="Ex : BC-2026-0457"
                className="mt-1"
                data-testid="convert-bc-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Indispensable pour le dépôt sur Chorus Pro
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" />
                N° d'engagement
              </Label>
              <Input
                value={engagement}
                onChange={(e) => setEngagement(e.target.value)}
                placeholder="Ex : ENG-2026-1234"
                className="mt-1"
                data-testid="convert-engagement-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Facultatif — souvent demandé par la collectivité
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Délai de paiement (jours)</Label>
              <Input
                type="number"
                min="0"
                value={delaiPaiement}
                onChange={(e) => setDelaiPaiement(e.target.value)}
                className="mt-1"
                data-testid="convert-delai-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Collectivité : 30 j (loi LME) — Hôpital : 50 j
              </p>
            </div>
          </div>

          {/* Identique ou non */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <Label className="text-sm font-semibold mb-3 block">
              La facture correspond-elle exactement au devis ?
            </Label>
            <RadioGroup value={identical} onValueChange={setIdentical} className="space-y-2">
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-blue-300" htmlFor="rb-yes">
                <RadioGroupItem value="yes" id="rb-yes" className="mt-0.5" data-testid="convert-identical-yes" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Oui, identique au devis</p>
                  <p className="text-xs text-slate-500 mt-0.5">Les lignes et montants du devis sont repris tels quels</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:border-blue-300" htmlFor="rb-no">
                <RadioGroupItem value="no" id="rb-no" className="mt-0.5" data-testid="convert-identical-no" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Non, il y a des modifications</p>
                  <p className="text-xs text-slate-500 mt-0.5">J'ajuste les lignes ci-dessous (ajouts, suppressions, prix…)</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Editable table when not identical */}
          {identical === 'no' && (
            <div key="lines-editor" className="border border-slate-200 rounded-xl overflow-hidden" data-testid="convert-lines-editor">
              <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-900">Lignes ajustées</p>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="convert-add-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="text-left p-2">Désignation</th>
                      <th className="text-right p-2 w-16">Qté</th>
                      <th className="text-left p-2 w-12">Unité</th>
                      <th className="text-right p-2 w-20">PU HT</th>
                      <th className="text-right p-2 w-14">TVA %</th>
                      <th className="text-right p-2 w-14">Rem %</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-1.5">
                          <Input value={l.designation} onChange={(e) => updateLine(i, 'designation', e.target.value)} className="h-8 text-xs" />
                        </td>
                        <td className="p-1.5">
                          <NumInputFR value={l.quantite} onChange={(n) => updateLine(i, 'quantite', n)} className="h-8 text-xs text-right" />
                        </td>
                        <td className="p-1.5">
                          <Input value={l.unite} onChange={(e) => updateLine(i, 'unite', e.target.value)} className="h-8 text-xs" />
                        </td>
                        <td className="p-1.5">
                          <NumInputFR value={l.prix_unitaire_ht} onChange={(n) => updateLine(i, 'prix_unitaire_ht', n)} className="h-8 text-xs text-right" />
                        </td>
                        <td className="p-1.5">
                          <NumInputFR value={l.tva_pct} onChange={(n) => updateLine(i, 'tva_pct', n)} className="h-8 text-xs text-right" />
                        </td>
                        <td className="p-1.5">
                          <NumInputFR value={l.remise_pct} onChange={(n) => updateLine(i, 'remise_pct', n)} className="h-8 text-xs text-right" />
                        </td>
                        <td className="p-1.5">
                          <button onClick={() => removeLine(i)} className="p-1 text-slate-400 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {lines.length === 0 && (
                      <tr><td colSpan={7} className="p-4 text-center text-slate-400">Aucune ligne — cliquez sur « Ajouter une ligne »</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="p-3 bg-slate-50 border-t border-slate-200">
                <Label className="text-xs font-semibold">Notes sur les modifications (facultatif)</Label>
                <Textarea
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 text-xs" rows={2}
                  placeholder="Ex : Ligne ‘Pose carrelage cuisine’ retirée car réalisée par le client lui-même"
                  data-testid="convert-notes"
                />
              </div>
            </div>
          )}

          {/* Totaux */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-600">Total HT</p>
                <p className="text-lg font-bold text-slate-900">{fmt(totals.total_ht)} €</p>
              </div>
              <div>
                <p className="text-xs text-slate-600">TVA</p>
                <p className="text-lg font-bold text-slate-900">{fmt(totals.total_tva)} €</p>
              </div>
              <div>
                <p className="text-xs text-slate-600">Total TTC</p>
                <p className="text-lg font-bold text-blue-700">{fmt(totals.total_ttc)} €</p>
              </div>
            </div>
          </div>

          {/* Warning */}
          {identical === 'no' && (
            <div key="warning-modifications" className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                En modifiant la facture par rapport au devis, vous engagez votre responsabilité.
                Les écarts seront tracés dans le CRM et la note ci-dessus accompagnera la facture.
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={submitting || !bc.trim()}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="convert-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            {submitting ? 'Conversion…' : 'Créer la facture'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
